/**
 * PermissionsEditor — checkbox matrix for per-audience and global permissions.
 */

import { cn } from '@/utils/cn';
import type { UserPermissions, Audience } from '@/types';
import { AUDIENCE_LABELS } from '@/types';

interface PermissionsEditorProps {
  permissions: UserPermissions;
  onChange:    (permissions: UserPermissions) => void;
  disabled?:   boolean;
}

const AUDIENCE_PERMS = [
  { key: 'view',           label: 'View' },
  { key: 'add',            label: 'Add' },
  { key: 'edit',           label: 'Edit' },
  { key: 'delete',         label: 'Delete' },
  { key: 'uploadFiles',    label: 'Upload Files' },
  { key: 'removeFiles',    label: 'Remove Files' },
  { key: 'copyContent',    label: 'Copy Content' },
  { key: 'approveContent', label: 'Approve' },
  { key: 'publishContent', label: 'Publish' },
] as const;

const GLOBAL_PERMS = [
  { key: 'viewDashboard', label: 'View Dashboard' },
  { key: 'manageUsers',   label: 'Manage Users' },
  { key: 'viewAuditLogs', label: 'View Audit Logs' },
] as const;

const AUDIENCES: Audience[] = ['mothers', 'fathers', 'grandparents'];

export default function PermissionsEditor({ permissions, onChange, disabled }: PermissionsEditorProps) {
  const setAudiencePerm = (audience: Audience, perm: string, value: boolean) => {
    onChange({
      ...permissions,
      [audience]: { ...permissions[audience], [perm]: value },
    });
  };

  const setGlobalPerm = (perm: string, value: boolean) => {
    onChange({
      ...permissions,
      global: { ...permissions.global, [perm]: value },
    });
  };

  const toggleAll = (audience: Audience, value: boolean) => {
    const all = Object.fromEntries(AUDIENCE_PERMS.map(p => [p.key, value]));
    onChange({ ...permissions, [audience]: all as unknown as UserPermissions[Audience] });
  };

  return (
    <div className="space-y-5">
      {/* Per-audience permissions */}
      {AUDIENCES.map(audience => {
        const perms = permissions[audience];
        const allChecked = AUDIENCE_PERMS.every(p => perms[p.key]);
        return (
          <div key={audience} className="border border-surface-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between bg-surface-50 px-4 py-2.5 border-b border-surface-200">
              <span className="text-sm font-semibold text-surface-800">
                {AUDIENCE_LABELS[audience]}
              </span>
              <label className="flex items-center gap-1.5 text-xs text-surface-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={e => toggleAll(audience, e.target.checked)}
                  disabled={disabled}
                  className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                />
                All
              </label>
            </div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2 p-4">
              {AUDIENCE_PERMS.map(({ key, label }) => (
                <label
                  key={key}
                  className={cn(
                    'flex items-center gap-2 text-sm text-surface-700 cursor-pointer',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={perms[key] ?? false}
                    onChange={e => setAudiencePerm(audience, key, e.target.checked)}
                    disabled={disabled}
                    className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        );
      })}

      {/* Global permissions */}
      <div className="border border-surface-200 rounded-xl overflow-hidden">
        <div className="bg-surface-50 px-4 py-2.5 border-b border-surface-200">
          <span className="text-sm font-semibold text-surface-800">Global</span>
        </div>
        <div className="grid grid-cols-3 gap-x-4 gap-y-2 p-4">
          {GLOBAL_PERMS.map(({ key, label }) => (
            <label
              key={key}
              className={cn(
                'flex items-center gap-2 text-sm text-surface-700 cursor-pointer',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="checkbox"
                checked={permissions.global[key] ?? false}
                onChange={e => setGlobalPerm(key, e.target.checked)}
                disabled={disabled}
                className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
